import React from 'react'
import { useStableRandomIndex } from '../../hooks/useStableRandomIndex'
import cherryTree from '@/src/renderer/assets/images/calm/cherryTree.webp'
import lilyPad from '@/src/renderer/assets/images/calm/lilyPad.webp'
import zenGarden from '@/src/renderer/assets/images/calm/zenGarden.webp'
import bonsai from '@/src/renderer/assets/images/calm/bonsai.webp'
import meditationCushion from '@/src/renderer/assets/images/calm/meditationCushion.webp'
import wildflowerField from '@/src/renderer/assets/images/calm/wildflowerField.webp'
import moss from '@/src/renderer/assets/images/calm/moss.webp'
import dune from '@/src/renderer/assets/images/calm/dune.webp'
import tideLine from '@/src/renderer/assets/images/calm/tideLine.webp'
import marsh from '@/src/renderer/assets/images/calm/marsh.webp'
import glassMarbles from '@/src/renderer/assets/images/calm/glassMarbles.webp'
import vernalPool from '@/src/renderer/assets/images/calm/vernalPool.webp'
import penHolder from '@/src/renderer/assets/images/desk/penHolder.webp'
import gridPaper from '@/src/renderer/assets/images/desk/gridPaper.webp'
import noteSpike from '@/src/renderer/assets/images/desk/noteSpike.webp'
import stapler from '@/src/renderer/assets/images/desk/stapler.webp'
import notebook from '@/src/renderer/assets/images/desk/notebook.webp'
import flowClipboard from '@/src/renderer/assets/images/desk/flowClipboard.webp'
import checklistClipboard from '@/src/renderer/assets/images/desk/checklistClipboard.webp'
import writtenNote from '@/src/renderer/assets/images/project/writtenNote.webp'
import corkBoard from '@/src/renderer/assets/images/project/corkBoard.webp'
import monitor from '@/src/renderer/assets/images/project/monitor.webp'
import stickyPad from '@/src/renderer/assets/images/project/stickyPad.webp'
import qnPencilCase from '@/src/renderer/assets/images/quickNotes/pencilCase.webp'
import qnPenCup from '@/src/renderer/assets/images/quickNotes/penCup.webp'
import qnSketchingPencils from '@/src/renderer/assets/images/quickNotes/sketchingPencils.webp'
import qnOrigamiCrane from '@/src/renderer/assets/images/quickNotes/origamiCrane.webp'
import qnNotePad from '@/src/renderer/assets/images/quickNotes/notePad.webp'
import qnPen from '@/src/renderer/assets/images/quickNotes/pen.webp'
import qnPencilHolder from '@/src/renderer/assets/images/quickNotes/pencilHolder.webp'
import qnOrigamiPaper from '@/src/renderer/assets/images/quickNotes/origamiPaper.webp'
import './CalmIllustration.scss'

/** Die ruhigen Motive aus Figma (5461:195042, 5462:195052). Neue kommen einfach hinten dazu. */
export const CALM_ILLUSTRATIONS = [
  { id: 'cherryTree', src: cherryTree },
  { id: 'lilyPad', src: lilyPad },
  { id: 'zenGarden', src: zenGarden },
  { id: 'bonsai', src: bonsai },
  { id: 'meditationCushion', src: meditationCushion },
  { id: 'wildflowerField', src: wildflowerField },
  { id: 'moss', src: moss },
  { id: 'dune', src: dune },
  { id: 'tideLine', src: tideLine },
  { id: 'marsh', src: marsh },
  { id: 'glassMarbles', src: glassMarbles },
  { id: 'vernalPool', src: vernalPool },
] as const

/** Schreibtisch-Motive aus Figma (5462:195058) — der leere Zustand auf **Wiederkehrend**. */
export const DESK_ILLUSTRATIONS = [
  { id: 'penHolder', src: penHolder },
  { id: 'gridPaper', src: gridPaper },
  { id: 'noteSpike', src: noteSpike },
  { id: 'stapler', src: stapler },
  { id: 'notebook', src: notebook },
  { id: 'flowClipboard', src: flowClipboard },
  { id: 'checklistClipboard', src: checklistClipboard },
] as const

/** Projekt-Motive aus Figma (5462:195078) — der leere Zustand eines **Projekts**. */
export const PROJECT_ILLUSTRATIONS = [
  { id: 'writtenNote', src: writtenNote },
  { id: 'corkBoard', src: corkBoard },
  { id: 'monitor', src: monitor },
  { id: 'stickyPad', src: stickyPad },
] as const

/** Stifte, Block, Origami aus Figma (5467:215506) — der leere Zustand in **Kurz notiert**. */
export const QUICK_NOTES_ILLUSTRATIONS = [
  { id: 'pencilCase', src: qnPencilCase },
  { id: 'penCup', src: qnPenCup },
  { id: 'sketchingPencils', src: qnSketchingPencils },
  { id: 'origamiCrane', src: qnOrigamiCrane },
  { id: 'notePad', src: qnNotePad },
  { id: 'pen', src: qnPen },
  { id: 'pencilHolder', src: qnPencilHolder },
  { id: 'origamiPaper', src: qnOrigamiPaper },
] as const

const ILLUSTRATION_SETS = {
  calm: CALM_ILLUSTRATIONS,
  desk: DESK_ILLUSTRATIONS,
  project: PROJECT_ILLUSTRATIONS,
  quickNotes: QUICK_NOTES_ILLUSTRATIONS,
} as const

export interface CalmIllustrationProps {
  /** Kantenlänge in px. Die Dateien haben die doppelte Größe (296 px). */
  size?: number
  /** Welcher Motiv-Satz: Natur (Geschafft, Ruhend), Schreibtisch (Wiederkehrend), Projekt oder Kurz notiert. */
  set?: keyof typeof ILLUSTRATION_SETS
  /** Feste Wahl nach Name (`id` im Satz), z. B. `'gridPaper'` — unabhängig von der Reihenfolge. */
  motif?: string
  /** Feste Wahl statt Zufall — für Stories und Tests. */
  index?: number
  className?: string
}

/**
 * Ein ruhiges Motiv, zufällig aus dem gewählten Satz (`set`). Gewählt wird beim
 * Einhängen und dann behalten (`useStableRandomIndex`): Die Grafik wechselt beim nächsten Besuch, nicht
 * bei jedem Re-Render. Rein dekorativ (`alt=""`).
 *
 * @example
 * <EmptyState illustration={<CalmIllustration />} title={…} text={…} />
 * <EmptyState illustration={<CalmIllustration set="desk" />} title={…} text={…} />
 */
export const CalmIllustration: React.FC<CalmIllustrationProps> = ({ size = 148, set = 'calm', motif, index, className }) => {
  const illustrations: readonly { id: string; src: string }[] = ILLUSTRATION_SETS[set]
  const randomIndex = useStableRandomIndex(illustrations.length)
  const illustration =
    illustrations.find(i => i.id === motif) ?? illustrations[(index ?? randomIndex) % illustrations.length]
  return (
    <img
      className={['calm-illustration', className].filter(Boolean).join(' ')}
      src={illustration.src}
      data-illustration={illustration.id}
      width={size}
      height={size}
      alt=""
      draggable={false}
    />
  )
}
